import React, { useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
  addDoc,
  deleteDoc,
  Timestamp,
  deleteField,
} from 'firebase/firestore'
import { db } from '../utils/firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  Edit2,
  CheckCircle,
  XCircle,
  Loader,
  Save,
  Settings as SettingsIcon,
  Download,
  Send,
  Calendar,
  X,
  Mail,
  Search,
} from 'lucide-react'
import './HoaFeeTrackerSection.css'

/**
 * HOA Payment Tracker
 *
 * Lets an admin (1) configure whether their neighborhood collects an HOA
 * fee and at what cadence, then (2) track per-address payment status
 * for any given period.
 *
 * Data model:
 *   neighborhoods/{id}.hoaFee = {
 *     enabled: boolean,
 *     frequency: 'annual' | 'semiannual' | 'quarterly' | 'monthly',
 *     amount: number | null,       // dollars, optional
 *     updatedAt: Timestamp
 *   }
 *   neighborhoods/{id}/addresses/{addressId}.hoaPayments = {
 *     '2026': 'paid',
 *     '2026-Q1': 'paid',
 *     '2026-03': 'unpaid',
 *     ...
 *   }
 *
 * The period key format depends on frequency (see periodKey() helper).
 */
export default function HoaFeeTrackerSection({ userProfile }) {
  const neighborhoodId = userProfile?.neighborhoodId
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Neighborhood config
  const [hoaFee, setHoaFee] = useState(null) // null = not yet decided
  const [editingConfig, setEditingConfig] = useState(false)

  // Address list + payment data
  const [addresses, setAddresses] = useState([])
  const [selectedPeriod, setSelectedPeriod] = useState(null) // period key
  // Per-row UI state — note drafts the admin is currently typing, and
  // which row is currently sending a reminder (for spinner + disabled).
  const [noteDrafts, setNoteDrafts] = useState({}) // { addressId: 'text' }
  const [sendingReminder, setSendingReminder] = useState(null) // addressId
  const [toast, setToast] = useState('') // ephemeral success message
  // Scheduled-reminder records loaded from Firestore. Keyed by
  // `${addressId}:${period}` so the row lookup is O(1). Each value is
  // { id, sendAt, status } from the scheduledReminders subcollection.
  const [scheduledReminders, setScheduledReminders] = useState({})
  const [schedulingReminder, setSchedulingReminder] = useState(null) // addressId
  // Neighborhood display name, plumbed through to the email-template
  // preview so admins see their actual name in the {neighborhoodName}
  // slot rather than a placeholder.
  const [neighborhoodName, setNeighborhoodName] = useState('')
  // Reminder email templates were previously a collapsible card at the
  // bottom of the tracker. Now surfaced via an envelope icon in the
  // summary bar → modal, so the tracker's core flow (address list) sits
  // right below the header without competing UI.
  const [showTemplatesModal, setShowTemplatesModal] = useState(false)
  // Row filters — free-text search across address / resident name /
  // email, and a paid/unpaid chip toggle. Counts on the chips update
  // as rows change so admins can eyeball the split at a glance.
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // 'all' | 'paid' | 'unpaid'

  // Load neighborhood doc + addresses once on mount
  useEffect(() => {
    if (!neighborhoodId) return
    loadEverything()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodId])

  const loadEverything = async () => {
    setLoading(true)
    setError('')
    try {
      const neighRef = doc(db, 'neighborhoods', neighborhoodId)
      const [neighSnap, addrSnap, usersSnap, invitesSnap, schedSnap] = await Promise.all([
        getDoc(neighRef),
        getDocs(query(collection(db, 'neighborhoods', neighborhoodId, 'addresses'))),
        // Cross-reference with live users so we don't render stale
        // residentName / residentEmail snapshots left on address docs
        // after the original resident was removed. Mirrors how
        // ManageNeighborsSection derives the active resident list.
        getDocs(query(
          collection(db, 'users'),
          where('neighborhoodId', '==', neighborhoodId)
        )),
        // Also load invitations so we can distinguish "pending" (an
        // invitation exists for this address) from "vacant" (nobody's
        // even been invited). Mirrors ManageNeighborsSection's logic.
        getDocs(query(
          collection(db, 'invitations'),
          where('neighborhoodId', '==', neighborhoodId)
        )),
        // Pending scheduled reminders drive the row-level "Scheduled"
        // pill and toggle the button between Schedule / Cancel. We only
        // care about ones not yet sent — anything with status='sent'
        // has already fired.
        getDocs(query(
          collection(db, 'neighborhoods', neighborhoodId, 'scheduledReminders'),
          where('status', '==', 'scheduled')
        )),
      ])

      // Pull fee config; missing means "not yet configured"
      const data = neighSnap.exists() ? neighSnap.data() : {}
      setHoaFee(data.hoaFee || null)
      setNeighborhoodName(data.name || '')
      // If config doesn't exist yet, show the setup form
      setEditingConfig(!data.hoaFee)

      // Build a live-user lookup keyed by their canonical address string.
      // Some users won't have an `address` field; we just skip those.
      const liveUserByAddress = {}
      usersSnap.docs.forEach(d => {
        const u = d.data()
        const key = (u.address || '').trim().toLowerCase()
        if (!key) return
        // First user wins per address (typical case is one primary
        // resident per address; secondary household members can share).
        if (!liveUserByAddress[key]) {
          liveUserByAddress[key] = {
            id: d.id,
            name: u.name || [u.firstName, u.lastName].filter(Boolean).join(' '),
            email: u.email || null,
          }
        }
      })

      // Build a "has pending invitation" lookup keyed by address. We only
      // count invitations whose code is still usable (not yet redeemed
      // and not invalidated). This mirrors ManageNeighborsSection.
      const pendingInviteByAddress = {}
      invitesSnap.docs.forEach(d => {
        const inv = d.data()
        const key = (inv.address || '').trim().toLowerCase()
        if (!key) return
        if (inv.code && !inv.isUsed && !inv.invalidated) {
          pendingInviteByAddress[key] = true
        }
      })

      // Load addresses + their payment maps, overriding resident fields
      // with live user data when present, and tagging each row with one
      // of three resident states:
      //   - resident: an active user lives here
      //   - pending:  no user yet, but an active invitation has been sent
      //   - vacant:   no user, no pending invitation
      const addrList = addrSnap.docs.map(d => {
        const a = { id: d.id, ...d.data() }
        const addrString = (a.address || a.address1 || '').trim().toLowerCase()
        const live = liveUserByAddress[addrString]
        if (live) {
          a.residentName = live.name || null
          a.residentEmail = live.email || null
          a.residentUserId = live.id
          a._residentState = 'resident'
        } else {
          // Stale snapshot — wipe the display fields so we don't show
          // a name for someone who no longer lives here.
          a.residentName = null
          a.residentEmail = null
          a.residentUserId = null
          a._residentState = pendingInviteByAddress[addrString] ? 'pending' : 'vacant'
        }
        return a
      })

      // Sort by displayable address line
      addrList.sort((a, b) => {
        const aKey = (a.address1 || a.address || '').toLowerCase()
        const bKey = (b.address1 || b.address || '').toLowerCase()
        return aKey.localeCompare(bKey)
      })
      setAddresses(addrList)

      // Index scheduled reminders by "addressId:period" so a row can
      // look up its own scheduled entry in O(1).
      const schedMap = {}
      schedSnap.docs.forEach(d => {
        const s = d.data()
        if (!s.addressId || !s.period) return
        schedMap[`${s.addressId}:${s.period}`] = {
          id: d.id,
          sendAt: s.sendAt || null,
          status: s.status || 'scheduled',
        }
      })
      setScheduledReminders(schedMap)
    } catch (e) {
      console.error('HOA tracker load failed:', e)
      setError(e.message || 'Could not load HOA fee data.')
    } finally {
      setLoading(false)
    }
  }

  // Pick a sensible default period whenever the frequency changes.
  // Re-runs when fiscalYearStart changes too so period buckets track the
  // configured fiscal calendar.
  useEffect(() => {
    if (hoaFee?.enabled && hoaFee.frequency) {
      const fy = hoaFee.fiscalYearStart
      const options = periodOptions(hoaFee.frequency, fy)
      // Prefer the current period over future ones
      const today = new Date()
      const current = options.find(o => o.key === periodKey(hoaFee.frequency, today, fy))
      setSelectedPeriod(current?.key || options[0]?.key || null)
    }
  }, [hoaFee?.enabled, hoaFee?.frequency, hoaFee?.fiscalYearStart?.month, hoaFee?.fiscalYearStart?.day])

  const handleSaveConfig = async ({ enabled, frequency, amount, fiscalYearStart, dueDates }) => {
    if (!neighborhoodId) return
    setSaving(true)
    setError('')
    try {
      const cleanAmount = amount === '' || amount == null
        ? null
        : Number(amount)
      const cleanFyStart = fiscalYearStart
        ? {
            month: Math.min(Math.max(parseInt(fiscalYearStart.month, 10) || 1, 1), 12),
            day: Math.min(Math.max(parseInt(fiscalYearStart.day, 10) || 1, 1), 31),
          }
        : (hoaFee?.fiscalYearStart || { month: 1, day: 1 })
      // Clamp every due date's day to its month's real length + coerce
      // month/day into ints. Belt-and-suspenders — the form should
      // already produce sane values but a stray legacy doc might not.
      const effectiveFreq = enabled ? frequency : (hoaFee?.frequency || 'annual')
      const expectedLen = periodsPerYear(effectiveFreq)
      const source = Array.isArray(dueDates) && dueDates.length === expectedLen
        ? dueDates
        : generateDefaultDueDates(effectiveFreq, cleanFyStart)
      const cleanDueDates = source.map(d => {
        const month = Math.min(Math.max(parseInt(d.month, 10) || 1, 1), 12)
        const day = Math.min(
          Math.max(parseInt(d.day, 10) || 1, 1),
          daysInMonthFor(month)
        )
        return { month, day }
      })
      const payload = {
        enabled,
        frequency: effectiveFreq,
        amount: enabled ? (Number.isFinite(cleanAmount) ? cleanAmount : null) : null,
        // Explicit marker: from this save forward, `amount` is the
        // annual dues total. See perPeriodAmount() for how legacy docs
        // (missing this field) are interpreted.
        amountBasis: 'annual',
        fiscalYearStart: cleanFyStart,
        dueDates: cleanDueDates,
        updatedAt: serverTimestamp(),
      }
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        hoaFee: payload,
      })
      setHoaFee(payload)
      setEditingConfig(false)
    } catch (e) {
      console.error('HOA config save failed:', e)
      setError(e.message || 'Could not save HOA fee settings.')
    } finally {
      setSaving(false)
    }
  }

  // Persist a note for an address for the current period. Called on
  // input blur or Enter. Empty strings clear the field via FieldValue.delete
  // semantics — we just write empty for simplicity; consumers treat
  // empty + missing as "no note".
  const saveNote = async (address, value) => {
    if (!address.id || !selectedPeriod) return
    const trimmed = (value || '').trim()
    const existing = address.hoaPaymentNotes?.[selectedPeriod] || ''
    if (trimmed === existing) return // no-op

    // Optimistic update
    setAddresses(prev => prev.map(a => {
      if (a.id !== address.id) return a
      return {
        ...a,
        hoaPaymentNotes: { ...(a.hoaPaymentNotes || {}), [selectedPeriod]: trimmed },
      }
    }))

    try {
      await updateDoc(
        doc(db, 'neighborhoods', neighborhoodId, 'addresses', address.id),
        { [`hoaPaymentNotes.${selectedPeriod}`]: trimmed }
      )
    } catch (e) {
      console.error('Note save failed:', e)
      setError('Could not save note. Please try again.')
    }
  }

  // Fire off the branded reminder email via the Cloud Function. The
  // function does its own permission check + Mailgun send. We confirm
  // before sending so an accidental click can't email a resident.
  const sendReminder = async (address) => {
    if (!address.id || !selectedPeriod) return
    if (!address.residentEmail) {
      setError(`No resident email on file for ${address.address1 || address.address}.`)
      return
    }

    const label = periodOptions(hoaFee.frequency, hoaFee.fiscalYearStart)
      .find(o => o.key === selectedPeriod)?.label || selectedPeriod
    const confirmed = window.confirm(
      `Send a reminder to ${address.residentName || address.residentEmail}`
      + `\nat ${address.residentEmail}`
      + `\nfor ${label}?`
    )
    if (!confirmed) return

    setSendingReminder(address.id)
    setError('')
    try {
      const fns = getFunctions()
      const callable = httpsCallable(fns, 'sendHoaPaymentReminder')
      await callable({
        neighborhoodId,
        addressId: address.id,
        period: selectedPeriod,
        periodLabel: label,
      })
      setToast(`Reminder sent to ${address.residentEmail}`)
      setTimeout(() => setToast(''), 3500)
      // Optimistic "Email sent {date}" caption update — the Cloud
      // Function also writes hoaReminderLog.{period} on the address doc,
      // so on next full load we'll pick up the authoritative timestamp.
      // Using a plain JS Date here keeps the row-render code simple
      // (it already handles both Firestore Timestamps and Date objects).
      const now = new Date()
      setAddresses(prev => prev.map(a => {
        if (a.id !== address.id) return a
        return {
          ...a,
          hoaReminderLog: {
            ...(a.hoaReminderLog || {}),
            [selectedPeriod]: now,
          },
        }
      }))
    } catch (e) {
      console.error('Reminder send failed:', e)
      setError(e?.message || 'Could not send reminder. Please try again.')
    } finally {
      setSendingReminder(null)
    }
  }

  // Schedule (or cancel) the auto-reminder email for the *next* period.
  // Stored in neighborhoods/{id}/scheduledReminders. A daily Cloud
  // Function (see functions/index.js processScheduledReminders) picks up
  // any row whose sendAt has passed and hasn't been sent yet, then fires
  // the same email as the manual "Remind" button.
  const scheduleNextReminder = async (address) => {
    if (!address.id || !selectedPeriod || !hoaFee?.enabled) return
    if (!address.residentEmail) {
      setError(`No resident email on file for ${address.address1 || address.address}.`)
      return
    }

    const nextKey = nextPeriodKey(hoaFee.frequency, selectedPeriod)
    if (!nextKey) {
      setError('Could not compute the next period key.')
      return
    }
    const nextLabel = labelForPeriodKey(hoaFee.frequency, nextKey, hoaFee.fiscalYearStart)

    // Send 1 month before the start of the next period. Anchored to the
    // 1st of the previous month so residents get a full 30-day heads-up
    // regardless of whether the period key is monthly / quarterly / etc.
    const nextStart = startDateOfPeriod(hoaFee.frequency, nextKey, hoaFee.fiscalYearStart)
    if (!nextStart) {
      setError('Could not compute the next due date.')
      return
    }
    const sendAt = new Date(nextStart)
    sendAt.setMonth(sendAt.getMonth() - 1)

    const confirmed = window.confirm(
      `Schedule a reminder email to ${address.residentName || address.residentEmail} for ${nextLabel}?`
      + `\n\nEmail will be sent on ${sendAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`
      + ' (1 month before the next period begins).'
    )
    if (!confirmed) return

    setSchedulingReminder(address.id)
    setError('')
    try {
      const ref = await addDoc(
        collection(db, 'neighborhoods', neighborhoodId, 'scheduledReminders'),
        {
          neighborhoodId,
          addressId: address.id,
          // Denormalized for the Cloud Function so it doesn't have to
          // re-resolve users/addresses at send time. If the address's
          // resident changes before sendAt, the function re-reads for
          // freshness — this is just a fallback.
          addressLine: address.address1 || address.address || '',
          residentEmail: address.residentEmail,
          residentName: address.residentName || '',
          period: nextKey,
          periodLabel: nextLabel,
          sendAt: Timestamp.fromDate(sendAt),
          status: 'scheduled',
          createdAt: serverTimestamp(),
          createdBy: userProfile?.uid || null,
          // The period the admin was viewing when they scheduled this —
          // useful for auditing "who scheduled what and when".
          scheduledFromPeriod: selectedPeriod,
        }
      )
      setScheduledReminders(prev => ({
        ...prev,
        [`${address.id}:${nextKey}`]: {
          id: ref.id,
          sendAt: Timestamp.fromDate(sendAt),
          status: 'scheduled',
        },
      }))
      setToast(`Reminder scheduled for ${nextLabel}`)
      setTimeout(() => setToast(''), 3500)
    } catch (e) {
      console.error('Schedule failed:', e)
      setError(e?.message || 'Could not schedule the reminder. Please try again.')
    } finally {
      setSchedulingReminder(null)
    }
  }

  const cancelScheduledReminder = async (address) => {
    if (!address.id || !hoaFee?.enabled) return
    const nextKey = nextPeriodKey(hoaFee.frequency, selectedPeriod)
    if (!nextKey) return
    const entry = scheduledReminders[`${address.id}:${nextKey}`]
    if (!entry?.id) return
    const confirmed = window.confirm('Cancel the scheduled reminder?')
    if (!confirmed) return
    setSchedulingReminder(address.id)
    try {
      await deleteDoc(doc(
        db, 'neighborhoods', neighborhoodId, 'scheduledReminders', entry.id
      ))
      setScheduledReminders(prev => {
        const next = { ...prev }
        delete next[`${address.id}:${nextKey}`]
        return next
      })
    } catch (e) {
      console.error('Cancel failed:', e)
      setError('Could not cancel the scheduled reminder.')
    } finally {
      setSchedulingReminder(null)
    }
  }

  const togglePaymentStatus = async (address) => {
    if (!hoaFee?.enabled || !selectedPeriod || !address.id) return
    const current = address.hoaPayments?.[selectedPeriod] || 'unpaid'
    const next = current === 'paid' ? 'unpaid' : 'paid'
    const priorPaidAt = address.hoaPaymentPaidAt?.[selectedPeriod] || null
    const now = new Date()

    // Optimistic update — flip the status AND set/clear the timestamp
    // so the caption under the button appears immediately. Server
    // write below replaces the local Date with a Firestore Timestamp
    // once it lands, but the row-render code handles both shapes.
    setAddresses(prev => prev.map(a => {
      if (a.id !== address.id) return a
      const nextPaidAt = { ...(a.hoaPaymentPaidAt || {}) }
      if (next === 'paid') {
        nextPaidAt[selectedPeriod] = now
      } else {
        delete nextPaidAt[selectedPeriod]
      }
      return {
        ...a,
        hoaPayments: { ...(a.hoaPayments || {}), [selectedPeriod]: next },
        hoaPaymentPaidAt: nextPaidAt,
      }
    }))

    try {
      // Persist status + timestamp in one round-trip. Use dot-notation
      // paths so we only touch the fields we care about — safer than
      // rewriting the whole hoaPayments / hoaPaymentPaidAt maps.
      const updates = { [`hoaPayments.${selectedPeriod}`]: next }
      if (next === 'paid') {
        updates[`hoaPaymentPaidAt.${selectedPeriod}`] = serverTimestamp()
      } else {
        // Clear the paid-at when reverting to unpaid — the old date
        // becomes meaningless (and misleading) once the row goes back
        // to unpaid.
        updates[`hoaPaymentPaidAt.${selectedPeriod}`] = deleteField()
      }
      await updateDoc(
        doc(db, 'neighborhoods', neighborhoodId, 'addresses', address.id),
        updates
      )
    } catch (e) {
      console.error('Payment toggle failed:', e)
      // Revert on error — status AND timestamp so what's on screen
      // matches what actually persisted.
      setAddresses(prev => prev.map(a => {
        if (a.id !== address.id) return a
        const revertedPaidAt = { ...(a.hoaPaymentPaidAt || {}) }
        if (priorPaidAt) {
          revertedPaidAt[selectedPeriod] = priorPaidAt
        } else {
          delete revertedPaidAt[selectedPeriod]
        }
        return {
          ...a,
          hoaPayments: { ...(a.hoaPayments || {}), [selectedPeriod]: current },
          hoaPaymentPaidAt: revertedPaidAt,
        }
      }))
      setError('Could not update payment status. Please try again.')
    }
  }

  // Build a CSV for every period that falls within the year of the
  // currently-selected period and trigger a browser download. Columns:
  // Address, Resident, Email, [period1 status], [period1 note], ...
  // Sized for a typical spreadsheet — Excel/Numbers/Sheets all open it.
  const exportYearCsv = () => {
    if (!hoaFee?.enabled || !selectedPeriod) return
    const year = yearFromPeriodKey(selectedPeriod)
    const allOptions = periodOptions(hoaFee.frequency, hoaFee.fiscalYearStart)
    const yearPeriods = allOptions
      .filter(o => yearFromPeriodKey(o.key) === year)
      // Render in chronological order for the CSV (oldest period first)
      .reverse()

    const header = [
      'Address',
      'Resident',
      'Email',
      ...yearPeriods.flatMap(p => [`${p.label} status`, `${p.label} note`]),
    ]
    const rows = addresses.map(addr => {
      const cells = [
        addr.address1 || addr.address || '',
        addr.residentName || '',
        addr.residentEmail || '',
      ]
      for (const p of yearPeriods) {
        const status = addr.hoaPayments?.[p.key] || 'unpaid'
        const note = addr.hoaPaymentNotes?.[p.key] || ''
        cells.push(status, note)
      }
      return cells
    })

    const csv = [header, ...rows].map(row => row.map(csvEscape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hoa-fees-${year}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Stats for the currently selected period. Collected/expected use the
  // per-period amount so the numbers match the reality of what
  // residents pay per bucket — not the full annual total.
  const stats = useMemo(() => {
    if (!hoaFee?.enabled || !selectedPeriod) return null
    const trackable = addresses.filter(a => a.id)
    const paid = trackable.filter(a => a.hoaPayments?.[selectedPeriod] === 'paid').length
    const total = trackable.length
    const perPeriod = perPeriodAmount(hoaFee)
    const collected = perPeriod != null ? paid * perPeriod : null
    const expected = perPeriod != null ? total * perPeriod : null
    return { paid, total, collected, expected, perPeriod }
  }, [addresses, hoaFee, selectedPeriod])

  // Rows the table actually shows after search + status filter. Empty
  // selectedPeriod (still loading) falls back to all addresses so we
  // don't blank the table mid-load. Search matches on address, resident
  // name, and resident email — same fields Manage Households searches.
  const visibleAddresses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return addresses.filter(addr => {
      if (statusFilter !== 'all' && selectedPeriod) {
        const paid = addr.hoaPayments?.[selectedPeriod] === 'paid'
        if (statusFilter === 'paid' && !paid) return false
        if (statusFilter === 'unpaid' && paid) return false
      }
      if (!q) return true
      const haystack = [
        addr.address1,
        addr.address,
        addr.residentName,
        addr.residentEmail,
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [addresses, searchQuery, statusFilter, selectedPeriod])

  if (!neighborhoodId) {
    return (
      <div className="section">
        <h2>HOA Payment Tracker</h2>
        <p>You need to be tied to a neighborhood to track HOA fees.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="section">
        <h2>HOA Payment Tracker</h2>
        <div className="loading-state">
          <Loader className="spinner" size={28} />
          <p>Loading…</p>
        </div>
      </div>
    )
  }

  // --- Render: setup form OR tracker ---
  return (
    <div className="section hoa-tracker-section">
      <h2 data-walk="hoa-config">HOA Payment Tracker</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Setup / Edit Config */}
      {(editingConfig || !hoaFee) && (
        <HoaConfigForm
          initial={hoaFee}
          saving={saving}
          onCancel={hoaFee ? () => setEditingConfig(false) : null}
          onSave={handleSaveConfig}
          onExport={hoaFee?.enabled ? exportYearCsv : null}
        />
      )}

      {/* No HOA fee — display state */}
      {hoaFee && !editingConfig && !hoaFee.enabled && (
        <div className="hoa-no-fee-card">
          <p><strong>This neighborhood doesn't collect an HOA fee.</strong></p>
          <p className="muted">If that changes, you can turn tracking on at any time.</p>
          <button className="btn-outline" onClick={() => setEditingConfig(true)}>
            <Edit2 size={16} /> Change setting
          </button>
        </div>
      )}

      {/* Active tracker */}
      {hoaFee?.enabled && !editingConfig && (
        <>
          <div className="hoa-config-summary">
            <div className="hoa-config-summary-text">
              <span className="muted">Fee:</span>{' '}
              <strong>
                {(() => {
                  const annual = annualAmountOf(hoaFee)
                  if (annual == null) return 'Amount not set'
                  const perPeriod = perPeriodAmount(hoaFee)
                  // For non-annual cadences, show the resident-facing
                  // per-period amount as the headline and the annual
                  // total as a supporting figure.
                  if (hoaFee.frequency !== 'annual' && perPeriod != null) {
                    return `$${formatAmount(perPeriod)}/${periodUnitLabel(hoaFee.frequency)} ($${formatAmount(annual)}/year)`
                  }
                  return `$${formatAmount(annual)}/year`
                })()}
              </strong>
              {' · '}
              <span className="muted">Frequency:</span>{' '}
              <strong>{frequencyLabel(hoaFee.frequency)}</strong>
            </div>
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button
                className="btn-outline small"
                onClick={() => setShowTemplatesModal(true)}
                title="Customize reminder email templates"
                aria-label="Reminder email templates"
              >
                <Mail size={14} />
              </button>
              <button className="btn-outline small" onClick={() => setEditingConfig(true)}>
                <SettingsIcon size={14} /> Edit
              </button>
            </div>
          </div>

          {/* Period selector + stats + export */}
          <div className="hoa-tracker-controls">
            <label className="hoa-period-label">
              Period
              <select
                value={selectedPeriod || ''}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                {periodOptions(hoaFee.frequency, hoaFee.fiscalYearStart).map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </label>

            {stats && (
              <div className="hoa-stats">
                <span>
                  <strong>{stats.paid}</strong> of <strong>{stats.total}</strong> paid
                </span>
                {stats.collected != null && (
                  <span>
                    {' · '}
                    <strong>${formatAmount(stats.collected)}</strong> collected
                    <span className="muted">
                      {' of '}${formatAmount(stats.expected)}
                    </span>
                  </span>
                )}
              </div>
            )}

            <button
              className="btn-outline"
              onClick={exportYearCsv}
              title="Download a CSV of every period in this year"
            >
              <Download size={14} /> Export {yearFromPeriodKey(selectedPeriod) || ''} (CSV)
            </button>
          </div>

          {toast && (
            <div className="hoa-toast-popup" role="status" aria-live="polite">
              <CheckCircle size={18} />
              <span>{toast}</span>
            </div>
          )}

          {/* Search + status filter — same styling as Manage
              Households. Counts on the chips reflect the current
              period's paid/unpaid split so admins can see the numbers
              without doing the math. */}
          {addresses.length > 0 && (
            <div className="filters-row" style={{ marginTop: 12 }}>
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search addresses or residents..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="clear-btn"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="status-filters">
                <button
                  className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  All ({stats?.total ?? addresses.length})
                </button>
                <button
                  className={`filter-chip ${statusFilter === 'paid' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('paid')}
                >
                  Paid ({stats?.paid ?? 0})
                </button>
                <button
                  className={`filter-chip ${statusFilter === 'unpaid' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('unpaid')}
                >
                  Unpaid ({(stats?.total ?? 0) - (stats?.paid ?? 0)})
                </button>
              </div>
            </div>
          )}

          {/* Address table */}
          <div className="hoa-address-list">
            {addresses.length === 0 ? (
              <div className="empty-state">
                <p>No addresses found for this neighborhood yet.</p>
              </div>
            ) : visibleAddresses.length === 0 ? (
              <div className="empty-state">
                <p>
                  No addresses match this filter.
                  {searchQuery && ' Try clearing the search.'}
                </p>
              </div>
            ) : (
              <table className="hoa-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Resident</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAddresses.map(addr => {
                    const status = addr.hoaPayments?.[selectedPeriod] === 'paid' ? 'paid' : 'unpaid'
                    const canToggle = !!addr.id // skip rows without a subcollection id
                    const savedNote = addr.hoaPaymentNotes?.[selectedPeriod] || ''
                    const draftKey = `${addr.id}:${selectedPeriod}`
                    const draftValue = noteDrafts[draftKey] !== undefined
                      ? noteDrafts[draftKey]
                      : savedNote
                    const isSending = sendingReminder === addr.id
                    // Look up any scheduled reminder for this row's
                    // *next* period. Only relevant on paid rows — the
                    // Schedule button is hidden otherwise.
                    const nextKey = hoaFee?.frequency
                      ? nextPeriodKey(hoaFee.frequency, selectedPeriod)
                      : null
                    const scheduled = nextKey
                      ? scheduledReminders[`${addr.id}:${nextKey}`]
                      : null
                    const isScheduling = schedulingReminder === addr.id
                    const scheduledSendDate = scheduled?.sendAt
                      ? (typeof scheduled.sendAt.toDate === 'function'
                          ? scheduled.sendAt.toDate()
                          : new Date(scheduled.sendAt))
                      : null
                    // Most recent manual reminder for the currently-viewed
                    // period. Firestore returns Timestamps (with .toDate)
                    // on load; the optimistic path writes a plain Date.
                    // Handle both shapes so the caption doesn't blank
                    // out between the click and the next full reload.
                    const lastSentRaw = addr.hoaReminderLog?.[selectedPeriod]
                    const lastSentDate = lastSentRaw
                      ? (typeof lastSentRaw.toDate === 'function'
                          ? lastSentRaw.toDate()
                          : (lastSentRaw instanceof Date ? lastSentRaw : new Date(lastSentRaw)))
                      : null
                    // When this row was marked paid (if it is). Handle
                    // both Firestore Timestamp (post-reload) and JS Date
                    // (optimistic click-time) shapes — same pattern the
                    // reminder timestamp uses.
                    const paidAtRaw = addr.hoaPaymentPaidAt?.[selectedPeriod]
                    const paidAtDate = paidAtRaw
                      ? (typeof paidAtRaw.toDate === 'function'
                          ? paidAtRaw.toDate()
                          : (paidAtRaw instanceof Date ? paidAtRaw : new Date(paidAtRaw)))
                      : null
                    return (
                      <tr key={addr.id || addr.address}>
                        <td>{addr.address1 || addr.address || '—'}</td>
                        <td className="muted">
                          {addr.residentName
                            ? addr.residentName
                            : (
                              <span className={`hoa-vacant-pill ${addr._residentState === 'pending' ? 'pending' : ''}`}>
                                {addr._residentState === 'pending' ? 'Pending' : 'Vacant'}
                              </span>
                            )}
                        </td>
                        <td>
                          <span className={`hoa-status-badge ${status}`}>
                            {status === 'paid' ? (
                              <>
                                <CheckCircle size={14} /> Paid
                              </>
                            ) : (
                              <>
                                <XCircle size={14} /> Unpaid
                              </>
                            )}
                          </span>
                        </td>
                        <td className="hoa-note-cell">
                          <input
                            type="text"
                            className="hoa-note-input"
                            value={draftValue}
                            disabled={!canToggle}
                            onChange={(e) => setNoteDrafts(prev => ({
                              ...prev,
                              [draftKey]: e.target.value,
                            }))}
                            onBlur={(e) => {
                              saveNote(addr, e.target.value)
                              setNoteDrafts(prev => {
                                const { [draftKey]: _, ...rest } = prev
                                return rest
                              })
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.target.blur()
                            }}
                          />
                        </td>
                        <td className="hoa-actions-cell">
                          {/* Flex column so the "Paid on …" caption
                              sits directly under the toggle, matching
                              the "Email sent …" pattern used by the
                              Remind button. flex-end keeps the button
                              at its natural width so paid + unpaid
                              rows look consistent. */}
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                            <button
                              className={`btn-outline small ${status === 'paid' ? 'danger' : ''}`}
                              disabled={!canToggle}
                              onClick={() => togglePaymentStatus(addr)}
                            >
                              {status === 'paid' ? 'Mark Unpaid' : 'Mark Paid'}
                            </button>
                            {status === 'paid' && paidAtDate && (
                              <span
                                className="muted"
                                style={{ fontSize: 11 }}
                                title={paidAtDate.toLocaleString()}
                              >
                                Paid {paidAtDate.toLocaleDateString(undefined, {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                })}
                              </span>
                            )}
                          </div>
                          {status === 'unpaid' && (
                            // Wrapper is `position: relative` so the
                            // "Emailed …" caption below can hang off
                            // absolutely, keeping the Remind button in
                            // exactly the same spot before and after the
                            // send (otherwise the button visibly shifts
                            // vertically when the caption appears).
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                className="btn-outline small"
                                disabled={!canToggle || !addr.residentEmail || isSending}
                                onClick={() => sendReminder(addr)}
                                title={addr.residentEmail
                                  ? `Email ${addr.residentEmail}`
                                  : 'No resident email on file'}
                              >
                                {isSending
                                  ? <><Loader size={12} className="spinner" /> Sending…</>
                                  : <><Send size={12} /> Remind</>}
                              </button>
                              {/* Most recent send for this period. Absent on rows
                                  that were never reminded (initial state). Uses
                                  a compact date-only format so it doesn't crowd
                                  the row. Absolute-positioned so the row height
                                  and button position stay stable. */}
                              {lastSentDate && (
                                <span
                                  className="muted"
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    marginTop: 2,
                                    // Sized + centered to roughly match
                                    // the Remind button's width. Short
                                    // numeric date (e.g. 8/6/26) keeps
                                    // it compact; the full timestamp is
                                    // still available in the tooltip.
                                    fontSize: 10,
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={lastSentDate.toLocaleString()}
                                >
                                  Emailed {lastSentDate.toLocaleDateString(undefined, {
                                    month: 'numeric', day: 'numeric', year: '2-digit',
                                  })}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Schedule button shows for BOTH paid and
                              unpaid rows so admins can queue a heads-up
                              reminder for the next period regardless of
                              current status. On unpaid rows it sits next
                              to the manual "Remind" button; on paid rows
                              it stands alone. */}
                          {nextKey && (
                            scheduled ? (
                              // Already scheduled — show a compact
                              // "Scheduled" pill that doubles as a cancel
                              // affordance on click, plus a caption
                              // underneath spelling out the send date so
                              // admins don't have to hover the tooltip.
                              <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                <button
                                  className="btn-outline small"
                                  disabled={!canToggle || isScheduling}
                                  onClick={() => cancelScheduledReminder(addr)}
                                  title={scheduledSendDate
                                    ? `Scheduled to send ${scheduledSendDate.toLocaleDateString()} — click to cancel`
                                    : 'Cancel scheduled reminder'}
                                >
                                  {isScheduling
                                    ? <><Loader size={12} className="spinner" /> Canceling…</>
                                    : <><X size={12} /> Scheduled</>}
                                </button>
                                {scheduledSendDate && (
                                  <span
                                    className="muted"
                                    style={{ fontSize: 11 }}
                                    title={scheduledSendDate.toLocaleString()}
                                  >
                                    Set for {scheduledSendDate.toLocaleDateString(undefined, {
                                      month: 'short', day: 'numeric', year: 'numeric',
                                    })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <button
                                className="btn-outline small"
                                disabled={!canToggle || !addr.residentEmail || isScheduling}
                                onClick={() => scheduleNextReminder(addr)}
                                title={addr.residentEmail
                                  ? `Auto-email ${addr.residentEmail} 1 month before ${labelForPeriodKey(hoaFee.frequency, nextKey, hoaFee.fiscalYearStart)}`
                                  : 'No resident email on file'}
                              >
                                {isScheduling
                                  ? <><Loader size={12} className="spinner" /> Scheduling…</>
                                  : <><Calendar size={12} /> Schedule Next</>}
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Reminder-email customization — surfaced via the envelope
              icon in the summary bar above rather than a permanent card
              at the bottom of the tracker, so admins hit the address
              list immediately without scrolling past optional UI. */}
          {showTemplatesModal && (
            <TemplatesModal
              onClose={() => setShowTemplatesModal(false)}
            >
              <ReminderTemplatesCard
                neighborhoodId={neighborhoodId}
                hoaFee={hoaFee}
                neighborhoodName={neighborhoodName}
                defaultOpen
                onSaved={(updatedTemplates) => {
                  setHoaFee(prev => ({ ...prev, emailTemplates: updatedTemplates }))
                }}
              />
            </TemplatesModal>
          )}

          {/* Super-admin-only: reconcile any stale
              residentEmail / residentName snapshots on address docs
              against live users. Runs the backfillAddressResidents
              callable — safe to click repeatedly since it no-ops when
              already in sync. */}
          {userProfile?.isSuperAdmin && (
            <BackfillResidentsButton neighborhoodId={neighborhoodId} />
          )}
        </>
      )}
    </div>
  )
}

// ============================================================
// Templates modal wrapper
// ============================================================
// Thin overlay that hosts the ReminderTemplatesCard. Kept minimal on
// purpose — closing on backdrop click, esc key, or the header X, and
// stopping propagation on the inner surface so clicks inside don't
// dismiss the modal. Styled inline to avoid needing yet another CSS
// hook for a rarely-shown surface.
function TemplatesModal({ children, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '32px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-card)',
          borderRadius: 12,
          width: 'min(760px, 100%)',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Reminder email templates</h3>
          <button
            onClick={onClose}
            className="btn-outline small"
            aria-label="Close"
            title="Close"
            style={{ padding: '4px 8px' }}
          >
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 16 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// One-off super-admin maintenance button. Wraps the
// backfillAddressResidents callable + confirmation + result summary.
// ============================================================
function BackfillResidentsButton({ neighborhoodId }) {
  const [running, setRunning] = useState(false)
  // Which scope the last run used, so the result caption is unambiguous.
  const [runScope, setRunScope] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  // scope: 'current' — only this neighborhood
  //        'all'     — every neighborhood in the project (super-admin
  //                    only path; can be many reads, so we prompt hard)
  const runBackfill = async (scope) => {
    const isAll = scope === 'all'
    const promptText = isAll
      ? 'Sync EVERY neighborhood?\n\n'
        + "This walks every address in every neighborhood and corrects "
        + "stale resident snapshots. Can be slow if you have a lot of "
        + "neighborhoods. Safe to run — it no-ops when data is already "
        + "in sync."
      : 'Sync every address in THIS neighborhood only?\n\n'
        + "Corrects any stale resident email / name snapshots for this "
        + "neighborhood."
    if (!window.confirm(promptText)) return
    setRunning(true)
    setRunScope(scope)
    setError('')
    setResult(null)
    try {
      const fns = getFunctions()
      const call = httpsCallable(fns, 'backfillAddressResidents')
      // Passing { neighborhoodId: null } triggers the all-neighborhoods
      // path in the Cloud Function.
      const res = await call({ neighborhoodId: isAll ? null : neighborhoodId })
      setResult(res.data)
    } catch (e) {
      console.error('Backfill failed:', e)
      setError(e?.message || 'Backfill failed.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div style={{
      marginTop: 24,
      padding: 14,
      border: '1px dashed #CBD5E1',
      borderRadius: 8,
      background: 'var(--color-bg-alt)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>
        Super admin · Backfill address residents
      </div>
      <p className="muted small" style={{ margin: '0 0 10px 0' }}>
        One-off maintenance: syncs every address's resident snapshot against
        the live user for that address. Fixes cases where the address doc
        was still pointing at an old invitation email (e.g. before the
        auto-sync trigger existed).
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn-outline small"
          onClick={() => runBackfill('current')}
          disabled={running}
        >
          {running && runScope === 'current'
            ? <><Loader size={12} className="spinner" /> Running…</>
            : 'Run for this neighborhood'}
        </button>
        <button
          type="button"
          className="btn-outline small"
          onClick={() => runBackfill('all')}
          disabled={running}
        >
          {running && runScope === 'all'
            ? <><Loader size={12} className="spinner" /> Running (all)…</>
            : 'Run for ALL neighborhoods'}
        </button>
      </div>
      {result && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {runScope === 'all' ? 'Across all neighborhoods: ' : 'This neighborhood: '}
          scanned <strong>{result.scanned}</strong>, updated <strong>{result.updated}</strong>,
          cleared <strong>{result.cleared}</strong>, unchanged <strong>{result.unchanged}</strong>.
        </div>
      )}
      {error && (
        <div className="error-message" style={{ marginTop: 8 }}>{error}</div>
      )}
    </div>
  )
}

// ============================================================
// Reminder email templates section
// ============================================================
function ReminderTemplatesCard({ neighborhoodId, hoaFee, neighborhoodName, onSaved, defaultOpen = false }) {
  // Collapsed by default when embedded in the tracker page (legacy
  // layout); the modal presents it with `defaultOpen` so admins don't
  // have to click twice to start editing.
  const [open, setOpen] = useState(defaultOpen)
  // When rendered inside the modal (defaultOpen) we drop the outer
  // border/toggle chrome — the modal already provides those. When
  // embedded on-page (legacy) we keep the collapsible card.
  const embedded = defaultOpen
  return (
    <div
      className="hoa-templates-card"
      style={embedded ? {
        background: 'transparent',
      } : {
        marginTop: 24,
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        background: 'var(--color-bg-card)',
      }}
    >
      {!embedded && (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '14px 18px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          <span>Reminder email templates</span>
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
            {open ? 'Hide' : 'Show & customize'}
          </span>
        </button>
      )}
      {open && (
        <div style={{ padding: embedded ? 0 : '0 18px 18px 18px' }}>
          <p className="muted small" style={{ marginTop: 0 }}>
            These messages go out to residents. Two templates: the "Remind"
            button uses the past-due one, the "Schedule Next" auto-send uses
            the upcoming one. Placeholders substitute at send time — leave
            a field blank to fall back to the default.
          </p>
          <ReminderTemplateEditor
            kind="pastDue"
            heading="Past-due reminder (manual)"
            neighborhoodId={neighborhoodId}
            hoaFee={hoaFee}
            neighborhoodName={neighborhoodName}
            onSaved={onSaved}
          />
          <ReminderTemplateEditor
            kind="upcoming"
            heading="Upcoming payment (scheduled, ~1 month ahead)"
            neighborhoodId={neighborhoodId}
            hoaFee={hoaFee}
            neighborhoodName={neighborhoodName}
            onSaved={onSaved}
          />
        </div>
      )}
    </div>
  )
}

function ReminderTemplateEditor({ kind, heading, neighborhoodId, hoaFee, neighborhoodName, onSaved }) {
  const stored = hoaFee?.emailTemplates?.[kind]
  const defaults = DEFAULT_REMINDER_TEMPLATES[kind]
  // Draft state — starts with either the neighborhood's saved override
  // OR the default text so admins can *edit* the defaults instead of
  // staring at a blank field. On save, if the value matches the default
  // verbatim, we still write empty string so future default changes
  // automatically flow through (see handleSave below).
  const [subject, setSubject] = useState(stored?.subject || defaults.subject)
  const [body, setBody] = useState(stored?.body || defaults.body)
  const [saving, setSaving] = useState(false)
  const [savedNote, setSavedNote] = useState('')
  const [error, setError] = useState('')

  // Live preview values — pulled from the neighborhood + a per-period
  // amount example so the admin sees something realistic.
  const previewVars = useMemo(() => {
    const perPeriod = perPeriodAmount(hoaFee)
    const amountFormatted = perPeriod != null
      ? `$${formatAmount(perPeriod)}`
      : ''
    // Peek at the current period for a realistic label; fall back to
    // the FY-current key if we can't compute one.
    const currentKey = hoaFee?.frequency
      ? periodKey(hoaFee.frequency, new Date(), hoaFee.fiscalYearStart)
      : ''
    const periodLabel = hoaFee?.frequency
      ? labelForPeriodKey(hoaFee.frequency, currentKey, hoaFee.fiscalYearStart)
      : ''
    return {
      firstName: 'Jane',
      residentName: 'Jane Smith',
      addressLine: '123 Elm St',
      neighborhoodName: neighborhoodName || 'your neighborhood',
      periodLabel: periodLabel || 'Q3 2026',
      amountDue: amountFormatted,
    }
  }, [hoaFee, neighborhoodName])

  const resolved = resolveTemplate(kind, { subject, body })
  const previewSubject = substitutePlaceholders(resolved.subject, previewVars)
  const previewBody = substitutePlaceholders(resolved.body, previewVars)

  const handleSave = async () => {
    if (!neighborhoodId) return
    setSaving(true)
    setError('')
    try {
      // If the admin's current text is byte-for-byte the default, store
      // empty string. That way the Cloud Function's "empty → default"
      // fallback keeps working AND if we ever tweak the default copy,
      // unmodified neighborhoods automatically inherit the new default
      // instead of being pinned to today's wording.
      const trimmedSubject = subject.trim()
      const trimmedBody = body.trim()
      const nextTemplate = {
        subject: trimmedSubject === defaults.subject.trim() ? '' : trimmedSubject,
        body:    trimmedBody    === defaults.body.trim()    ? '' : trimmedBody,
      }
      const nextTemplates = {
        ...(hoaFee?.emailTemplates || {}),
        [kind]: nextTemplate,
      }
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        'hoaFee.emailTemplates': nextTemplates,
      })
      onSaved?.(nextTemplates)
      setSavedNote('Saved.')
      setTimeout(() => setSavedNote(''), 2000)
    } catch (e) {
      console.error('Template save failed:', e)
      setError(e?.message || 'Could not save the template.')
    } finally {
      setSaving(false)
    }
  }

  const handleRestoreDefaults = () => {
    if (!window.confirm('Restore the default subject and body for this reminder?')) return
    // Fill the fields with the default text (not blank) so the admin
    // can see and tweak it. The save-time equality check treats
    // unchanged defaults as "empty" on disk.
    setSubject(defaults.subject)
    setBody(defaults.body)
  }

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 8,
      padding: 14,
      marginTop: 14,
      background: 'var(--color-bg-alt)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <h4 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-primary)' }}>{heading}</h4>
        <button
          type="button"
          className="btn-outline small"
          onClick={handleRestoreDefaults}
          disabled={saving || (!subject && !body)}
          title="Clear both fields to fall back to the default template"
        >
          Restore defaults
        </button>
      </div>

      <label style={{ display: 'block', fontSize: 13, color: 'var(--color-text-primary)' }}>
        Subject
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={DEFAULT_REMINDER_TEMPLATES[kind].subject}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 4,
            padding: '8px 10px',
            border: '1px solid #CBD5E1',
            borderRadius: 6,
            fontSize: 14,
          }}
        />
      </label>

      <label style={{ display: 'block', fontSize: 13, color: 'var(--color-text-primary)', marginTop: 10 }}>
        Message body
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={DEFAULT_REMINDER_TEMPLATES[kind].body}
          rows={7}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 4,
            padding: '8px 10px',
            border: '1px solid #CBD5E1',
            borderRadius: 6,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
      </label>

      <p className="muted small" style={{ marginTop: 8 }}>
        Placeholders:{' '}
        {REMINDER_PLACEHOLDERS.map((p, i) => (
          <React.Fragment key={p.key}>
            <code style={{ fontSize: 11 }}>{`{${p.key}}`}</code>
            {i < REMINDER_PLACEHOLDERS.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}
      </p>

      {error && (
        <div className="error-message" style={{ marginTop: 8 }}>{error}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <><Loader size={14} className="spinner" /> Saving…</> : <><Save size={14} /> Save</>}
        </button>
        {savedNote && <span className="muted small">{savedNote}</span>}
      </div>

      {/* Live preview — shows the same header/footer chrome the actual
          email uses (logo, portal button) so admins get an accurate
          sense of the result. Amount Due row hidden when unset, matching
          the real email. */}
      <div style={{ marginTop: 16 }}>
        <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-secondary)', margin: '0 0 6px 0' }}>
          Preview
        </p>
        <div style={{
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          background: 'var(--color-bg-card)',
          padding: 16,
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            <strong>Subject:</strong> {previewSubject || <em className="muted">(no subject)</em>}
          </p>
          <div style={{
            background: 'var(--color-bg-alt)',
            padding: 20,
            borderRadius: 6,
            border: '1px solid #EEF2F7',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: '#2e7d32',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
              }}>NT</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32', marginTop: 6 }}>
                NeighborTopia
              </div>
            </div>
            {/* Whitespace-preserving render for the body so \n\n
                creates paragraph breaks the same way Mailgun's text/html
                rendering does. */}
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
              {previewBody || <em className="muted">(no message body)</em>}
            </div>
            <div style={{
              background: 'var(--color-bg-card)',
              border: '2px dashed #d97706',
              borderRadius: 8,
              padding: '12px 16px',
              margin: '16px 0',
            }}>
              <div style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', fontWeight: 600 }}>Address</div>
              <div style={{ color: '#78350f', fontWeight: 600 }}>{previewVars.addressLine}</div>
              <div style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', fontWeight: 600, marginTop: 6 }}>Period</div>
              <div style={{ color: '#78350f', fontWeight: 600 }}>{previewVars.periodLabel}</div>
              {previewVars.amountDue && (
                <>
                  <div style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', fontWeight: 600, marginTop: 6 }}>Amount Due</div>
                  <div style={{ color: '#78350f', fontWeight: 600 }}>{previewVars.amountDue}</div>
                </>
              )}
            </div>
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <div style={{
                display: 'inline-block',
                background: '#4CAF50',
                color: '#fff',
                padding: '10px 24px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
              }}>Open the Portal</div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0 }}>
              Thanks,<br />{previewVars.neighborhoodName} via NeighborTopia
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Config form — used for both first-time setup and edits.
// ============================================================
function HoaConfigForm({ initial, saving, onCancel, onSave, onExport }) {
  const [enabled, setEnabled] = useState(initial?.enabled ?? null)
  const [frequency, setFrequency] = useState(initial?.frequency || 'annual')
  // Field represents annual dues going forward. When editing a legacy
  // doc that stored per-period amount, we convert up to the annual
  // total on first render so the admin sees a consistent number.
  const [amount, setAmount] = useState(() => {
    const annual = annualAmountOf(initial)
    return annual != null ? String(annual) : ''
  })
  // Fiscal year start. Stored on the doc as { month, day } — nil means
  // "not yet configured" which we treat as Jan 1 (identical to the
  // legacy calendar-year behavior, so no data migration is needed).
  const [fyMonth, setFyMonth] = useState(initial?.fiscalYearStart?.month || 1)
  const [fyDay, setFyDay] = useState(initial?.fiscalYearStart?.day || 1)
  // Clamp day when month changes so we don't leave e.g. Feb 30 selected.
  const daysInMonth = daysInMonthFor(fyMonth)
  const clampedFyDay = Math.min(fyDay, daysInMonth)
  if (clampedFyDay !== fyDay) {
    // Silently correct on the next render. Avoids invalid state without
    // pulling in an extra useEffect.
    setFyDay(clampedFyDay)
  }

  // Due dates — one entry per period. Regenerated whenever the
  // frequency changes (the array length changes with it). Preserves
  // existing values on first load if the stored array's length still
  // matches the current frequency's period count.
  const initialDueDates = (() => {
    const stored = Array.isArray(initial?.dueDates) ? initial.dueDates : null
    const expected = periodsPerYear(initial?.frequency || 'annual')
    if (stored && stored.length === expected) return stored
    return generateDefaultDueDates(
      initial?.frequency || 'annual',
      initial?.fiscalYearStart || DEFAULT_FY_START
    )
  })()
  const [dueDates, setDueDates] = useState(initialDueDates)
  // For monthly, the whole array collapses to one "day of month" value
  // — showing 12 pickers would be overwhelming. Derived from the first
  // entry's day, or 1 if unset.
  const monthlyDueDay = dueDates[0]?.day || 1

  // When frequency (or fyStart) changes, regenerate defaults if the
  // array length no longer matches. Skips when the length is already
  // correct so admin edits aren't clobbered by fyStart tweaks.
  useEffect(() => {
    const expected = periodsPerYear(frequency)
    if (dueDates.length !== expected) {
      setDueDates(generateDefaultDueDates(frequency, { month: fyMonth, day: clampedFyDay }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency])

  // Clamp every stored day against its month's length so a Feb 30 that
  // slipped in from a legacy doc doesn't render an out-of-range option.
  const safeDueDates = dueDates.map(d => ({
    month: d.month,
    day: Math.min(d.day, daysInMonthFor(d.month)),
  }))

  // Show the export-first warning only when there's existing data worth
  // exporting (i.e., the admin is editing a previously-enabled config,
  // not setting one up for the first time).
  const showExportWarning = !!(initial?.enabled && onExport)

  return (
    <div className="hoa-config-form">
      {showExportWarning && (
        <div className="hoa-export-warning">
          <div className="hoa-export-warning-text">
            <strong>Heads up:</strong> Before changing the frequency or amount,
            we recommend exporting the current data for your records. Frequency
            changes use different period keys (e.g. <code>2026-Q1</code> vs.
            <code> 2026-03</code>), and amount changes recalculate the
            collected vs. expected totals in future exports.
          </div>
          <button
            type="button"
            className="btn-outline"
            onClick={onExport}
          >
            <Download size={14} /> Export current data (CSV)
          </button>
        </div>
      )}

      <div className="hoa-config-block">
        <h3>Does your neighborhood collect an HOA fee?</h3>
        <div className="hoa-yesno">
          <button
            type="button"
            className={`hoa-yesno-btn ${enabled === true ? 'selected' : ''}`}
            onClick={() => setEnabled(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`hoa-yesno-btn ${enabled === false ? 'selected' : ''}`}
            onClick={() => setEnabled(false)}
          >
            No
          </button>
        </div>
      </div>

      {enabled === true && (
        <>
          <div className="hoa-config-block">
            <label>
              Frequency
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                <option value="annual">Annually (once per year)</option>
                <option value="semiannual">Semi-annually (twice per year)</option>
                <option value="quarterly">Quarterly (every 3 months)</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
          </div>

          {/* Fiscal year start — anchors quarterly/semiannual/annual
              period buckets. Default Jan 1 mirrors legacy behavior. */}
          <div className="hoa-config-block">
            <label>
              Fiscal Year Start
              <div style={{ display: 'flex', gap: 8, marginTop: 6, maxWidth: 320 }}>
                <select
                  value={fyMonth}
                  onChange={(e) => setFyMonth(parseInt(e.target.value, 10))}
                  style={{ flex: 2 }}
                  aria-label="Month"
                >
                  {['January','February','March','April','May','June',
                    'July','August','September','October','November','December']
                    .map((name, idx) => (
                      <option key={idx + 1} value={idx + 1}>{name}</option>
                    ))}
                </select>
                <select
                  value={clampedFyDay}
                  onChange={(e) => setFyDay(parseInt(e.target.value, 10))}
                  style={{ flex: 1 }}
                  aria-label="Day"
                >
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <p className="muted small">
                Anchors the {frequency === 'monthly' ? 'annual reporting boundary' : 'quarterly, semi-annual, and annual periods'} used across the tracker. Defaults to January 1.
              </p>
            </label>
          </div>

          {/* Due dates — one per period. Monthly collapses to a single
              "day of month" input because 12 month/day pickers would
              overwhelm; the other frequencies list each period. */}
          <div className="hoa-config-block">
            <label>Due dates</label>
            {frequency === 'monthly' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, maxWidth: 320 }}>
                <span className="muted small">Due on day</span>
                <select
                  value={monthlyDueDay}
                  onChange={(e) => {
                    const day = parseInt(e.target.value, 10)
                    // Regenerate all 12 entries with the same day, clamped
                    // to each month's length so Feb doesn't overflow.
                    setDueDates(Array.from({ length: 12 }, (_, i) => {
                      const month = i + 1
                      return { month, day: Math.min(day, daysInMonthFor(month)) }
                    }))
                  }}
                  style={{ width: 80 }}
                  aria-label="Day of month"
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <span className="muted small">of each month.</span>
              </div>
            ) : (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {safeDueDates.map((d, idx) => {
                  const daysThisMonth = daysInMonthFor(d.month)
                  return (
                    <div
                      key={idx}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span
                        className="muted small"
                        style={{ minWidth: 150, textAlign: 'right' }}
                      >
                        {periodSlotLabel(frequency, idx, { month: fyMonth, day: clampedFyDay })}
                      </span>
                      <select
                        value={d.month}
                        onChange={(e) => {
                          const month = parseInt(e.target.value, 10)
                          const next = dueDates.slice()
                          next[idx] = {
                            month,
                            day: Math.min(d.day, daysInMonthFor(month)),
                          }
                          setDueDates(next)
                        }}
                        style={{ flex: 2, maxWidth: 160 }}
                        aria-label={`${periodSlotLabel(frequency, idx, { month: fyMonth, day: clampedFyDay })} month`}
                      >
                        {['Jan','Feb','Mar','Apr','May','Jun',
                          'Jul','Aug','Sep','Oct','Nov','Dec']
                          .map((name, i) => (
                            <option key={i + 1} value={i + 1}>{name}</option>
                          ))}
                      </select>
                      <select
                        value={d.day}
                        onChange={(e) => {
                          const next = dueDates.slice()
                          next[idx] = { month: d.month, day: parseInt(e.target.value, 10) }
                          setDueDates(next)
                        }}
                        style={{ flex: 1, maxWidth: 80 }}
                        aria-label={`${periodSlotLabel(frequency, idx, { month: fyMonth, day: clampedFyDay })} day`}
                      >
                        {Array.from({ length: daysThisMonth }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>
                  )
                })}
              </div>
            )}
            <p className="muted small" style={{ marginTop: 6 }}>
              When each period's dues are due. Defaults to the start of each period.
            </p>
          </div>

          <div className="hoa-config-block">
            <label>
              Annual amount per address <span className="muted">(optional)</span>
              <div className="hoa-amount-input">
                <span className="hoa-amount-prefix">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {/* Derived per-period preview. Only shown when the admin
                  has typed a number AND the cadence isn't annual (in
                  which case per-period == annual and the extra line
                  would just repeat the input). */}
              {(() => {
                const n = Number(amount)
                if (!amount || !Number.isFinite(n) || n <= 0) return null
                if (frequency === 'annual') return null
                const perPeriod = n / periodsPerYear(frequency)
                return (
                  <p className="muted small" style={{ marginTop: 4 }}>
                    Residents will owe <strong>${formatAmount(perPeriod)}</strong>{' '}
                    per {periodUnitLabel(frequency)}
                    {' '}(${formatAmount(n)}/year ÷ {periodsPerYear(frequency)}).
                  </p>
                )
              })()}
              <p className="muted small">
                Enter the full <em>yearly</em> HOA dues per household. The
                tracker splits it into what residents owe each period.
              </p>
            </label>
          </div>
        </>
      )}

      <div className="hoa-config-actions">
        {onCancel && (
          <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn-primary"
          disabled={enabled === null || saving}
          onClick={() => onSave({
            enabled,
            frequency,
            amount,
            fiscalYearStart: { month: fyMonth, day: clampedFyDay },
            dueDates: safeDueDates,
          })}
        >
          {saving ? <Loader size={16} className="spinner" /> : <Save size={16} />}
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  )
}

// ============================================================
// Reminder email templates
// ============================================================
//
// Each neighborhood can override the subject + body of the two HOA
// reminder emails. When missing/empty on the doc, we fall back to the
// defaults defined here. Kept in-file rather than a separate module so
// the preview panel and the Cloud Function each have their own copy —
// duplication is small and these strings change rarely.
//
// Placeholders supported in both subject and body:
//   {firstName}         resident's first name ("Jane")
//   {residentName}      resident's full name ("Jane Smith")
//   {addressLine}       street address ("123 Elm St")
//   {neighborhoodName}  neighborhood name ("Linden Park")
//   {periodLabel}       period the reminder is about ("Q3 2026")
//   {amountDue}         formatted per-period amount ("$50") — empty when unset
const REMINDER_PLACEHOLDERS = [
  { key: 'firstName',        example: 'Jane' },
  { key: 'residentName',     example: 'Jane Smith' },
  { key: 'addressLine',      example: '123 Elm St' },
  { key: 'neighborhoodName', example: 'Linden Park' },
  { key: 'periodLabel',      example: 'Q3 2026' },
  { key: 'amountDue',        example: '$50' },
]

const DEFAULT_REMINDER_TEMPLATES = {
  // Fires when an admin clicks "Remind" on an unpaid row.
  pastDue: {
    subject: 'Reminder: HOA payment for {neighborhoodName}',
    body:
      'Hi {firstName}!\n\n' +
      'This is a friendly reminder from your neighborhood admin: the HOA ' +
      'payment for {addressLine} for {periodLabel} is currently marked as ' +
      'unpaid.\n\n' +
      'Please reach out to your HOA/Neighborhood Admin to resolve this.',
  },
  // Fires ~1 month before the next period begins (via the scheduled
  // Cloud Function).
  upcoming: {
    subject: 'Upcoming HOA payment for {neighborhoodName}',
    body:
      'Hi {firstName}!\n\n' +
      'This is an advance heads-up from your neighborhood admin at ' +
      '{neighborhoodName}: your next HOA payment is coming up in about a ' +
      'month.\n\n' +
      'Please reach out to your HOA/Neighborhood Admin with any questions.',
  },
}

// Merge a stored template (or partial) with defaults so every field is
// non-empty. Empty strings from the doc fall back to the default too,
// which matches the "cleared → restore default" UX in the editor.
function resolveTemplate(kind, stored) {
  const dflt = DEFAULT_REMINDER_TEMPLATES[kind] || { subject: '', body: '' }
  return {
    subject: (stored?.subject && stored.subject.trim()) || dflt.subject,
    body:    (stored?.body    && stored.body.trim())    || dflt.body,
  }
}

// Replace {placeholder} tokens with values from `vars`. Missing keys
// render as empty string — safer than leaving the raw {token} visible
// to a resident.
function substitutePlaceholders(template, vars) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => {
    return vars[key] != null ? String(vars[key]) : ''
  })
}

// ============================================================
// Helpers
// ============================================================
function frequencyLabel(freq) {
  switch (freq) {
    case 'monthly': return 'Monthly'
    case 'quarterly': return 'Quarterly'
    case 'semiannual': return 'Semi-annually'
    case 'annual':
    default: return 'Annually'
  }
}

// Payments per year for the configured cadence. Used to split an
// annual dues total into what a resident actually owes each period.
function periodsPerYear(freq) {
  switch (freq) {
    case 'monthly': return 12
    case 'quarterly': return 4
    case 'semiannual': return 2
    case 'annual':
    default: return 1
  }
}

// Short label for the per-period unit — used in the config form's
// "$X per {periodUnit}" hint and in the tracker summary.
function periodUnitLabel(freq) {
  switch (freq) {
    case 'monthly': return 'month'
    case 'quarterly': return 'quarter'
    case 'semiannual': return 'half-year'
    case 'annual':
    default: return 'year'
  }
}

// Resolve what a resident actually owes for a single tracked period,
// given the doc's `amount` and `amountBasis`.
//
// amountBasis history:
//   - undefined  → legacy docs written before Aug 2026; `amount` was
//                   entered by the admin as a per-period amount, so we
//                   return it as-is.
//   - 'annual'   → new docs; `amount` is annual dues, so divide by the
//                   number of periods per year.
//
// Returns null when the amount isn't set (admins can leave it blank).
function perPeriodAmount(hoaFee) {
  if (!hoaFee || hoaFee.amount == null) return null
  const n = Number(hoaFee.amount)
  if (!Number.isFinite(n)) return null
  if (hoaFee.amountBasis === 'annual') {
    return n / periodsPerYear(hoaFee.frequency)
  }
  return n // legacy: already per-period
}

// The annual total, regardless of how the field was stored. Used by
// the config form to pre-populate the "annual amount" field when
// editing a legacy doc.
function annualAmountOf(hoaFee) {
  if (!hoaFee || hoaFee.amount == null) return null
  const n = Number(hoaFee.amount)
  if (!Number.isFinite(n)) return null
  if (hoaFee.amountBasis === 'annual') return n
  // Legacy per-period entry → multiply back up for display.
  return n * periodsPerYear(hoaFee.frequency)
}

function formatAmount(n) {
  if (n == null) return ''
  // Show two decimals if non-integer, no decimals if round
  const isWhole = Number.isInteger(Number(n))
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

// Pull the four-digit year off any period key (`2026`, `2026-Q1`,
// `2026-H1`, `2026-03`). Returns the integer year, or null if we can't
// parse one — used by the CSV export to scope to the current year.
function yearFromPeriodKey(key) {
  if (!key) return null
  const m = String(key).match(/^(\d{4})/)
  return m ? parseInt(m[1], 10) : null
}

// Minimal RFC-4180-ish CSV escaping: wrap in double quotes if the value
// contains a comma, quote, or newline, and double up any embedded quotes.
function csvEscape(value) {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

// ============================================================
// Fiscal-year helpers
// ============================================================
//
// hoaFee.fiscalYearStart = { month: 1-12, day: 1-31 }
//
// When set to {1, 1} — the default and only value pre-Aug 2026 — the
// fiscal year lines up with the calendar year and every helper below
// produces exactly the same keys/labels/dates as before. That's
// intentional: existing hoaPayments maps stay valid with zero
// migration.
//
// For non-Jan-1 starts, the "fiscal year" a date belongs to is named by
// the calendar year in which the FY *starts*. So with fyStart Apr 1:
//   Apr 2026 – Mar 2027 → FY 2026 → key "2026", "2026-Q1"…"2026-Q4"
//   Jan 2027 – Mar 2027 still belong to FY 2026.
//
// Day-of-month is honored when deciding the FY boundary itself but not
// when slotting a date into a quarter/half — we still use the calendar
// month boundary. So fyStart Apr 15 means Apr 14 belongs to prior FY,
// but Apr 15 kicks off "Q1" that runs Apr 15 → mid-July.
const DEFAULT_FY_START = { month: 1, day: 1 }

function normalizeFyStart(fyStart) {
  const m = Math.min(Math.max(parseInt(fyStart?.month, 10) || 1, 1), 12)
  const d = Math.min(Math.max(parseInt(fyStart?.day, 10) || 1, 1), 31)
  return { month: m, day: d }
}

function fiscalYearOf(date, fyStart) {
  const fs = normalizeFyStart(fyStart)
  const y = date.getFullYear()
  const m = date.getMonth() + 1
  const d = date.getDate()
  const beforeStart =
    m < fs.month || (m === fs.month && d < fs.day)
  return beforeStart ? y - 1 : y
}

// 0-indexed month within the fiscal year — 0 == first month of FY.
// Uses calendar-month distance only, ignoring day-of-month so quarters
// stay 3-month wide even when the FY starts mid-month.
function monthOfFiscalYear(date, fyStart) {
  const fs = normalizeFyStart(fyStart)
  const m = date.getMonth() + 1
  return (m - fs.month + 12) % 12
}

// Given a fiscal-year integer, return the Date at the start of that FY.
function startDateOfFiscalYear(fyYear, fyStart) {
  const fs = normalizeFyStart(fyStart)
  return new Date(fyYear, fs.month - 1, fs.day)
}

// Number of days in a given 1-12 month, using 2001 as the non-leap
// reference year so Feb resolves to 28. Used by the FY-start day picker
// to keep the dropdown honest.
function daysInMonthFor(month) {
  // Day 0 of month+1 rolls back to the last day of `month`.
  return new Date(2001, month, 0).getDate()
}

// Default due dates when the admin hasn't set them yet. One entry per
// period in the year, anchored to the *start* of each period as the
// safest default (residents owe by the time the period begins). Admins
// can adjust each one after setup.
function generateDefaultDueDates(frequency, fyStart) {
  const fs = normalizeFyStart(fyStart)
  const n = periodsPerYear(frequency)
  const monthsPerPeriod = 12 / n
  const list = []
  for (let i = 0; i < n; i++) {
    // Walk forward from fyStart in period-length increments. Wrap the
    // month back into 1-12 range with modular arithmetic.
    const offset = i * monthsPerPeriod
    const rawMonth = fs.month + offset
    const month = ((rawMonth - 1) % 12) + 1
    // Clamp day to the number of days in that month (Feb 29 → 28 etc.).
    const day = Math.min(fs.day, daysInMonthFor(month))
    list.push({ month, day })
  }
  return list
}

// Human label for a period slot — used to label each due-date row in
// the config form. For monthly we don't render 12 rows so this is only
// called for annual / semiannual / quarterly.
function periodSlotLabel(frequency, index, fyStart) {
  const fs = normalizeFyStart(fyStart)
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun',
                      'Jul','Aug','Sep','Oct','Nov','Dec']
  const monthsPerPeriod = 12 / periodsPerYear(frequency)
  const startMonth = ((fs.month - 1 + index * monthsPerPeriod) % 12) + 1
  const endMonth = ((startMonth - 1 + monthsPerPeriod - 1) % 12) + 1
  switch (frequency) {
    case 'annual':
      return 'Annual dues'
    case 'semiannual':
      return `H${index + 1} (${monthNames[startMonth - 1]}–${monthNames[endMonth - 1]})`
    case 'quarterly':
      return `Q${index + 1} (${monthNames[startMonth - 1]}–${monthNames[endMonth - 1]})`
    default:
      return `Period ${index + 1}`
  }
}

// Given a period key + cadence, return the key for the *following*
// period. Used by the "Schedule next reminder" action so we don't have
// to walk the periodOptions list when the current period isn't in it.
function nextPeriodKey(frequency, key) {
  if (!key) return null
  const y = parseInt(String(key).slice(0, 4), 10)
  if (Number.isNaN(y)) return null
  switch (frequency) {
    case 'monthly': {
      // Key is "YYYY-MM". Roll month → next; year rolls if we hit Dec.
      const m = parseInt(String(key).slice(5, 7), 10)
      if (!m || m < 1 || m > 12) return null
      const nextM = m === 12 ? 1 : m + 1
      const nextY = m === 12 ? y + 1 : y
      return `${nextY}-${String(nextM).padStart(2, '0')}`
    }
    case 'quarterly': {
      // Key is "YYYY-QN".
      const q = parseInt(String(key).slice(6, 7), 10)
      if (!q || q < 1 || q > 4) return null
      const nextQ = q === 4 ? 1 : q + 1
      const nextY = q === 4 ? y + 1 : y
      return `${nextY}-Q${nextQ}`
    }
    case 'semiannual': {
      // Key is "YYYY-HN".
      const h = parseInt(String(key).slice(6, 7), 10)
      if (!h || (h !== 1 && h !== 2)) return null
      return h === 1 ? `${y}-H2` : `${y + 1}-H1`
    }
    case 'annual':
    default:
      // Key is "YYYY".
      return `${y + 1}`
  }
}

// Convert a period key back into the Date at the *start* of that period.
// The scheduler uses this to compute "one month before due" — we treat
// the start of the period as the due date, which is the strictest
// interpretation and lets residents get the reminder well before any
// grace window ends.
//
// For non-Jan-1 fiscal years:
//   - annual "2026" → FY 2026 start (e.g. Apr 1, 2026 if fyStart=Apr 1)
//   - quarterly "2026-Q1" → same as annual (Q1 begins at FY start)
//   - quarterly "2026-Q2" → 3 months after Q1
//   - semiannual "2026-H1" → FY start; "2026-H2" → 6 months later
//   - monthly "2026-03" → Mar 1, 2026 (still calendar month, unchanged)
function startDateOfPeriod(frequency, key, fyStart = DEFAULT_FY_START) {
  if (!key) return null
  const y = parseInt(String(key).slice(0, 4), 10)
  if (Number.isNaN(y)) return null
  const fs = normalizeFyStart(fyStart)
  switch (frequency) {
    case 'monthly': {
      // Monthly keys remain calendar-month indexed; no FY shift.
      const m = parseInt(String(key).slice(5, 7), 10)
      if (!m) return null
      return new Date(y, m - 1, 1)
    }
    case 'quarterly': {
      const q = parseInt(String(key).slice(6, 7), 10)
      if (!q) return null
      // Q1 starts at fyStart; each subsequent Q adds 3 calendar months.
      const d = new Date(y, fs.month - 1 + (q - 1) * 3, fs.day)
      return d
    }
    case 'semiannual': {
      const h = parseInt(String(key).slice(6, 7), 10)
      if (!h) return null
      return new Date(y, fs.month - 1 + (h === 1 ? 0 : 6), fs.day)
    }
    case 'annual':
    default:
      return startDateOfFiscalYear(y, fs)
  }
}

// Pretty label for a period key. Duplicated from periodOptions's
// formatting so we can label a key that isn't in the visible options list
// (e.g. a "next period" key that's beyond the current 12-month window).
function labelForPeriodKey(frequency, key, fyStart = DEFAULT_FY_START) {
  if (!key) return ''
  const fs = normalizeFyStart(fyStart)
  const start = startDateOfPeriod(frequency, key, fs)
  if (!start) return key
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const y = parseInt(String(key).slice(0, 4), 10) // FY year for quarterly/semiannual/annual
  const isCalendarFy = fs.month === 1 && fs.day === 1
  switch (frequency) {
    case 'monthly':
      return `${monthNames[start.getMonth()]} ${start.getFullYear()}`
    case 'quarterly': {
      const qLabel = String(key).slice(5) // "Q1".."Q4"
      // Range hint helps admins reason about non-standard FY quarters.
      const endMonth = start.getMonth() + 2
      const endDate = new Date(start.getFullYear(), endMonth, 1)
      const range = `${monthNames[start.getMonth()]}–${monthNames[endDate.getMonth()]}`
      return isCalendarFy
        ? `${qLabel} ${y}`
        : `${qLabel} FY${y} (${range})`
    }
    case 'semiannual': {
      const endMonth = start.getMonth() + 5
      const endDate = new Date(start.getFullYear(), endMonth, 1)
      const range = `${monthNames[start.getMonth()]}–${monthNames[endDate.getMonth()]}`
      return isCalendarFy
        ? `${start.getMonth() === 0 ? 'Jan–Jun' : 'Jul–Dec'} ${y}`
        : `${String(key).slice(5)} FY${y} (${range})`
    }
    case 'annual':
    default:
      if (isCalendarFy) return String(y)
      // Non-standard FY — surface both the FY name and its calendar span
      // so the admin can't mistake FY 2026 for calendar 2026.
      return `FY ${y} (${monthNames[start.getMonth()]} ${start.getFullYear()}–${monthNames[(start.getMonth() + 11) % 12]} ${start.getFullYear() + 1})`
  }
}

// Build the period key Firestore uses for hoaPayments map entries.
// The year embedded in the key is the *fiscal* year (which equals the
// calendar year for the default fyStart=Jan 1), so quarterly/semiannual/
// annual buckets are all anchored to the fiscal calendar.
function periodKey(frequency, date, fyStart = DEFAULT_FY_START) {
  const fs = normalizeFyStart(fyStart)
  const y = date.getFullYear()
  const m = date.getMonth() // 0-11
  switch (frequency) {
    case 'monthly':
      // Kept as calendar month so old data doesn't shift positions when
      // fyStart changes. Months are unambiguous regardless of FY.
      return `${y}-${String(m + 1).padStart(2, '0')}`
    case 'quarterly': {
      const fy = fiscalYearOf(date, fs)
      const monthInFy = monthOfFiscalYear(date, fs)
      return `${fy}-Q${Math.floor(monthInFy / 3) + 1}`
    }
    case 'semiannual': {
      const fy = fiscalYearOf(date, fs)
      const monthInFy = monthOfFiscalYear(date, fs)
      return `${fy}-H${monthInFy < 6 ? 1 : 2}`
    }
    case 'annual':
    default:
      return `${fiscalYearOf(date, fs)}`
  }
}

// Generate a list of plausible period options the admin can pick from.
// Always includes a year of past + a year of future periods so the admin
// can backfill or pre-mark upcoming payments.
function periodOptions(frequency, fyStart = DEFAULT_FY_START) {
  const fs = normalizeFyStart(fyStart)
  const today = new Date()
  const items = []
  const currentFy = fiscalYearOf(today, fs)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  switch (frequency) {
    case 'monthly': {
      // 12 months back, current, 12 months forward — sorted newest-first.
      for (let offset = -12; offset <= 12; offset++) {
        const d = new Date(today.getFullYear(), today.getMonth() + offset, 1)
        items.push({
          key: periodKey('monthly', d, fs),
          label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`,
          sort: d.getTime(),
        })
      }
      break
    }
    case 'quarterly': {
      for (let fy = currentFy - 1; fy <= currentFy + 1; fy++) {
        for (let q = 1; q <= 4; q++) {
          const key = `${fy}-Q${q}`
          items.push({
            key,
            label: labelForPeriodKey('quarterly', key, fs),
            sort: startDateOfPeriod('quarterly', key, fs).getTime(),
          })
        }
      }
      break
    }
    case 'semiannual': {
      for (let fy = currentFy - 1; fy <= currentFy + 1; fy++) {
        for (let h = 1; h <= 2; h++) {
          const key = `${fy}-H${h}`
          items.push({
            key,
            label: labelForPeriodKey('semiannual', key, fs),
            sort: startDateOfPeriod('semiannual', key, fs).getTime(),
          })
        }
      }
      break
    }
    case 'annual':
    default: {
      for (let fy = currentFy - 2; fy <= currentFy + 2; fy++) {
        items.push({
          key: `${fy}`,
          label: labelForPeriodKey('annual', `${fy}`, fs),
          sort: startDateOfFiscalYear(fy, fs).getTime(),
        })
      }
      break
    }
  }

  // Newest first — admins are usually interested in the current/most-recent
  // period rather than historical ones.
  items.sort((a, b) => b.sort - a.sort)
  return items.map(({ key, label }) => ({ key, label }))
}
